import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getKyun, deleteKyun, createKyun, updateKyun } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  {
    key: "shortDescription",
    label: "Summary",
    render: (value) => (value ? String(value).slice(0, 120) : "-"),
  },
  {
    key: "publishDate",
    label: "Publish Date",
    render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
];

const filters = [
  {
    key: "author",
    label: "Filter by Author",
    type: "search",
  },
];

const initialFormData = {
  title: "",
  shortDescription: "",
  content: "",
  author: "Maha Sabha",
  imageUrl: "",
  imageUrlsText: "",
  videoUrlsText: "",
};

const splitLines = (raw = "") =>
  String(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const toEditorPayload = (record) => {
  const mediaItems = Array.isArray(record?.mediaItems) ? record.mediaItems : [];
  const imageUrlsText = mediaItems
    .filter((item) => item?.type === "image" && item?.url)
    .map((item) => item.url)
    .join("\n");
  const videoUrlsText = mediaItems
    .filter((item) => item?.type === "video" && item?.url)
    .map((item) => item.url)
    .join("\n");

  return {
    title: record?.title || "",
    shortDescription: record?.shortDescription || "",
    content: record?.content || "",
    author: record?.author || "Maha Sabha",
    imageUrl: record?.imageUrl || "",
    imageUrlsText,
    videoUrlsText,
  };
};

const toApiPayload = (formData) => ({
  title: formData.title,
  shortDescription: formData.shortDescription,
  content: formData.content,
  author: formData.author,
  imageUrl: formData.imageUrl,
  imageUrls: splitLines(formData.imageUrlsText),
  videoUrls: splitLines(formData.videoUrlsText),
});

export default function Kyun() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData(toEditorPayload(record));
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (record) => {
    handleEdit(record);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = toApiPayload(formData);

      if (isEditMode && selectedRecord) {
        await updateKyun(selectedRecord._id, payload);
        alert("Kyun artikel updated successfully!");
      } else {
        await createKyun(payload);
        alert("Kyun artikel created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save Kyun artikel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Kyun"
          fetchData={getKyun}
          deleteData={deleteKyun}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search Kyun artikelen..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Kyun Artikel" : "Add New Kyun Artikel"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter article title"
              />
            </div>
            <div className="form-group">
              <label>Short Description * (max 300)</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                maxLength={300}
                rows="3"
                required
                placeholder="Short summary used in notifications and previews"
              />
            </div>
            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleFormChange}
                placeholder="Enter author name"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Content</h3>
            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleFormChange}
                rows="10"
                required
                placeholder="Enter Kyun content"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>Primary Image URL</label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Extra Image URLs (one per line)</label>
              <textarea
                name="imageUrlsText"
                value={formData.imageUrlsText}
                onChange={handleFormChange}
                rows="4"
                placeholder={"https://image-1.jpg\nhttps://image-2.jpg"}
              />
            </div>
            <div className="form-group">
              <label>Video URLs (one per line)</label>
              <textarea
                name="videoUrlsText"
                value={formData.videoUrlsText}
                onChange={handleFormChange}
                rows="4"
                placeholder="https://video-1.mp4"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update Kyun" : "Create Kyun"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
